package diana.soleil.design.iraniancanadienbazzar.core.data.firestore

import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CategoryRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ListingMetricsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ListingRequestRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.FollowRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ReviewRepository
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AuthService
import diana.soleil.design.iraniancanadienbazzar.core.firebase.FirebaseServices
import diana.soleil.design.iraniancanadienbazzar.core.firebase.uploadImageToStorage
import diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage
import diana.soleil.design.iraniancanadienbazzar.core.model.Address
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingChangeRequest
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingDetailsEdit
import diana.soleil.design.iraniancanadienbazzar.core.model.ClaimStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.MediaStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.Metros
import diana.soleil.design.iraniancanadienbazzar.core.model.ClaimReviewStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.ClaimRole
import diana.soleil.design.iraniancanadienbazzar.core.model.DailyListingCounters
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingCounters
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingMetric
import diana.soleil.design.iraniancanadienbazzar.core.model.Category
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingRequestType
import diana.soleil.design.iraniancanadienbazzar.core.model.Review
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.Query
import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.withTimeout
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map

/**
 * Business directory: listings, reviews, follows, listing requests, metrics, categories.
 *
 * Split out of the former 2,753-line FirestoreRepositories.kt (2026-08-02) — same code,
 * same behaviour, one file per domain. Shared collection names and helpers live in
 * FirestoreCommon.kt.
 */

/**
 * Live [BusinessRepository] over Cloud Firestore (GitLive). Only `approved` listings are queried
 * (Firestore rules also enforce this). Documents that fail to decode — e.g. legacy/seed rows whose
 * localized fields are bare strings rather than `{en,fr,fa}` objects — are skipped rather than failing
 * the whole stream (see `FirestoreDtos`).
 */
class FirestoreBusinessRepository(
    firebase: FirebaseServices,
    private val auth: AuthService,
) : BusinessRepository {

    private val firestore: FirebaseFirestore = firebase.firestore
    private val functions = firebase.functions

    override val currentUid: String? get() = auth.currentUser?.takeIf { !it.isAnonymous }?.uid

    override fun observeBusinesses(
        metroId: String?,
        categoryId: String?,
        country: String?,
    ): Flow<DataResult<List<Business>>> {
        var query: Query = firestore.collection(BUSINESSES).where { "status" equalTo STATUS_APPROVED }
        if (categoryId != null) query = query.where { "categoryId" equalTo categoryId }
        if (metroId != null) query = query.where { "metroId" equalTo metroId }
        // "All <country>": scope by country when no metro narrows it (a metro already implies its country).
        else if (country != null) query = query.where { "address.country" equalTo country }
        return query.toBusinessesResult()
    }

    override fun observeFeatured(metroId: String?, country: String?): Flow<DataResult<List<Business>>> {
        var query: Query = firestore.collection(BUSINESSES)
            .where { "status" equalTo STATUS_APPROVED }
            .where { "sponsorship.active" equalTo true }
        if (metroId != null) query = query.where { "metroId" equalTo metroId }
        else if (country != null) query = query.where { "address.country" equalTo country }
        return query.toBusinessesResult()
    }

    override fun observeNewest(metroId: String?, country: String?, limit: Int): Flow<DataResult<List<Business>>> {
        var query: Query = firestore.collection(BUSINESSES).where { "status" equalTo STATUS_APPROVED }
        if (metroId != null) query = query.where { "metroId" equalTo metroId }
        else if (country != null) query = query.where { "address.country" equalTo country }
        // Composite indexes (status, metroId|address.country, createdAt DESC) — firebase/firestore.indexes.json.
        return query.orderBy("createdAt", Direction.DESCENDING).limit(limit).toBusinessesResult()
    }

    override suspend fun getBusiness(id: String): DataResult<Business> = try {
        val snapshot = firestore.collection(BUSINESSES).document(id).get()
        if (!snapshot.exists) {
            DataResult.Failure(AppError.NotFound())
        } else {
            DataResult.Success(snapshot.decode<BusinessDoc>().toModel(snapshot.id))
        }
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun getBusinesses(ids: Collection<String>): List<Business> = coroutineScope {
        // Parallel point reads, not a corpus stream: K favorites cost K reads once, and per-doc
        // rules (approved-or-owner-or-admin) evaluate cleanly where a list query could not.
        ids.map { id -> async { (getBusiness(id) as? DataResult.Success)?.data } }
            .awaitAll()
            .filterNotNull()
    }

    override suspend fun submitBusiness(
        name: LocalizedText,
        categoryId: String,
        description: LocalizedText,
        addressLine: String,
        city: String,
        province: String,
        postalCode: String,
        metroId: String?,
        attributes: List<String>,
    ): DataResult<String> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        val primaryName = name.en?.takeIf { it.isNotBlank() }
            ?: name.fr?.takeIf { it.isNotBlank() }
            ?: name.fa?.takeIf { it.isNotBlank() }
        if (primaryName.isNullOrBlank() || categoryId.isBlank()) return DataResult.Failure(AppError.Unknown())
        return try {
            val ref = firestore.collection(BUSINESSES).add(
                BusinessCreateDoc(
                    name = primaryName.trim(),
                    nameLocalized = LocalizedTextDoc(en = name.en, fr = name.fr, fa = name.fa),
                    ownerUid = user.uid,
                    claimStatus = ClaimStatus.Claimed,
                    categoryId = categoryId,
                    description = LocalizedTextDoc(en = description.en, fr = description.fr, fa = description.fa),
                    status = STATUS_PENDING,
                    verified = false,
                    address = AddressDoc(
                        line = addressLine.trim(),
                        city = city.trim(),
                        province = province.trim(),
                        postalCode = postalCode.trim().ifBlank { null },
                    ),
                    // The address is ground truth for the listing's metro (an owner may add a Toronto
                    // business while browsing Vancouver) — the caller's active metro is the fallback.
                    metroId = Metros.forCity(city.trim())?.id ?: metroId,
                    attributes = attributes.distinct(),
                    mediaStatus = MediaStatus.CategoryPlaceholderPending,
                    createdAt = Timestamp.ServerTimestamp,
                ),
            )
            DataResult.Success(ref.id)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun updateBusinessAttributes(businessId: String, attributes: List<String>): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            firestore.collection(BUSINESSES).document(businessId)
                .update("attributes" to attributes.distinct())
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun deleteBusiness(businessId: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            firestore.collection(BUSINESSES).document(businessId).delete()
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun createPromotionCheckout(businessId: String, planId: String): DataResult<String> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            val result = functions.httpsCallable("createPromotionCheckout")
                .invoke(mapOf("businessId" to businessId, "plan" to planId))
            // Deserialize into a @Serializable type, NOT Map<String, Any?> — the latter throws on
            // Kotlin/JS (no serializer for Any?), which silently turned a successful call into a
            // "couldn't start checkout" on web.
            val url = result.data<PromotionCheckoutResult>().url
                ?: return DataResult.Failure(AppError.Unknown())
            DataResult.Success(url)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun updateBusinessPrivacy(
        businessId: String,
        hideExactAddress: Boolean,
        ownerHidden: Boolean,
    ): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            firestore.collection(BUSINESSES).document(businessId)
                .update("hideExactAddress" to hideExactAddress, "ownerHidden" to ownerHidden)
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun uploadBusinessMedia(
        businessId: String,
        cover: PickedImage?,
        logo: PickedImage?,
        gallery: List<PickedImage>,
    ): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        if (cover == null && logo == null && gallery.isEmpty()) return DataResult.Success(Unit)

        // Best-effort per image: a flaky connection must not throw away the ones that made it.
        // Whatever uploaded gets stamped; a single failure fails the RESULT (caller may warn) but
        // never the listing.
        var anyFailed = false
        suspend fun upload(image: PickedImage, label: String): String? = try {
            val ext = if (image.mimeType.contains("png", ignoreCase = true)) "png" else "jpg"
            val name = "$label-" + kotlin.random.Random.nextLong().toString().replace("-", "") + "." + ext
            uploadImageToStorage("$BUSINESSES/$businessId/$name", image.bytes, image.mimeType)
        } catch (t: Throwable) {
            anyFailed = true
            null
        }

        val coverUrl = cover?.let { upload(it, "cover") }
        val logoUrl = logo?.let { upload(it, "logo") }
        val galleryUrls = gallery.mapNotNull { upload(it, "gallery") }

        val updates = buildList<Pair<String, Any>> {
            coverUrl?.let { add("coverUrl" to it) }
            logoUrl?.let { add("logoUrl" to it) }
            // APPEND, never replace: an owner adding one photo from the editor used to wipe the gallery.
            if (galleryUrls.isNotEmpty()) {
                add("photos" to dev.gitlive.firebase.firestore.FieldValue.arrayUnion(*galleryUrls.toTypedArray()))
            }
            if (isNotEmpty()) add("mediaStatus" to "owner_media")
        }
        return try {
            if (updates.isNotEmpty()) {
                firestore.collection(BUSINESSES).document(businessId).update(*updates.toTypedArray())
            }
            if (anyFailed) DataResult.Failure(AppError.Unknown()) else DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun updateListingDetails(businessId: String, edit: ListingDetailsEdit): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        fun String?.clean(): String? = this?.trim()?.takeIf { it.isNotBlank() }
        return try {
            // Dotted field paths with plain values (not nested DTOs) so the update encodes identically
            // on Android, iOS and Kotlin/JS.
            firestore.collection(BUSINESSES).document(businessId).update(
                "serviceTitle.en" to edit.serviceTitle.en.clean(),
                "serviceTitle.fr" to edit.serviceTitle.fr.clean(),
                "serviceTitle.fa" to edit.serviceTitle.fa.clean(),
                "description.en" to edit.description.en.clean(),
                "description.fr" to edit.description.fr.clean(),
                "description.fa" to edit.description.fa.clean(),
                "phone" to edit.phone.clean(),
                "email" to edit.email.clean(),
                "website" to edit.website.clean(),
                "hours" to edit.hours.mapValues { it.value.trim() }.filterValues { it.isNotBlank() },
                "languagesSpoken" to edit.languagesSpoken.distinct(),
                "priceLevel" to edit.priceLevel?.coerceIn(1, 4),
                "offersChat" to edit.offersChat,
                "offersOrdering" to edit.offersOrdering,
            )
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun removeBusinessPhoto(businessId: String, photoUrl: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            firestore.collection(BUSINESSES).document(businessId)
                .update("photos" to dev.gitlive.firebase.firestore.FieldValue.arrayRemove(photoUrl))
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun requestListingChange(
        business: Business,
        name: String?,
        categoryId: String?,
        address: Address?,
    ): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        val newName = name?.trim()?.takeIf { it.isNotBlank() && it != business.name }
        val newCategory = categoryId?.takeIf { it.isNotBlank() && it != business.categoryId }
        val newAddress = address?.takeIf { it.line.isNotBlank() && it.city.isNotBlank() && it != business.address }
        if (newName == null && newCategory == null && newAddress == null) return DataResult.Success(Unit)
        fun Address.doc() = AddressDoc(
            line = line.trim(),
            city = city.trim(),
            province = province.trim(),
            postalCode = postalCode?.trim()?.ifBlank { null },
            country = country,
            // A moved listing's old pin would be wrong; the admin-approved copy drops lat/lng.
            lat = null,
            lng = null,
        )
        return try {
            firestore.collection(LISTING_CHANGE_REQUESTS).add(
                ListingChangeRequestCreateDoc(
                    businessId = business.id,
                    businessName = business.name,
                    requestedBy = user.uid,
                    proposedName = newName,
                    proposedCategoryId = newCategory,
                    proposedAddress = newAddress?.doc(),
                    currentName = business.name,
                    currentCategoryId = business.categoryId,
                    currentAddress = AddressDoc(
                        line = business.address.line,
                        city = business.address.city,
                        province = business.address.province,
                        postalCode = business.address.postalCode,
                        country = business.address.country,
                        lat = business.address.lat,
                        lng = business.address.lng,
                    ),
                    status = STATUS_PENDING,
                    createdAt = Timestamp.ServerTimestamp,
                ),
            )
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observePendingListingChanges(businessId: String): Flow<DataResult<List<ListingChangeRequest>>> =
        auth.authState.flatMapLatest { user ->
            if (user == null || user.isAnonymous) {
                flowOf(DataResult.Success(emptyList()))
            } else {
                // Equality-only filters (served by merged single-field indexes). `requestedBy` is part of
                // the rules proof: an owner may only list their own requests.
                firestore.collection(LISTING_CHANGE_REQUESTS)
                    .where { "requestedBy" equalTo user.uid }
                    .where { "businessId" equalTo businessId }
                    .where { "status" equalTo STATUS_PENDING }
                    .snapshots
                    .map { snap ->
                        DataResult.Success(
                            snap.documents.mapNotNull { doc ->
                                runCatching { doc.decode<ListingChangeRequestDoc>().toModel(doc.id) }.getOrNull()
                            },
                        ) as DataResult<List<ListingChangeRequest>>
                    }
                    .catch { emit(DataResult.Failure(it.toAppError())) }
            }
        }

    override suspend fun requestClaim(businessId: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            // changedOnly(['claimStatus','claimRequestedBy']) in rules — touch exactly those fields.
            firestore.collection(BUSINESSES).document(businessId).update(
                "claimStatus" to "claim_requested",
                "claimRequestedBy" to user.uid,
            )
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun submitClaim(
        businessId: String,
        businessName: String,
        claimantName: String,
        role: ClaimRole,
        contactEmail: String,
        contactPhone: String,
        message: String,
        proof: PickedImage?,
    ): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            // Proof goes up first: the claim doc is immutable to the claimant after create (rules),
            // so the URL must be known before the doc is written. The path is keyed by the claimant's
            // uid — that's what storage.rules authorizes on (claim-proofs/{uid} is claimant+admin only).
            val documentRef = proof?.let { image ->
                val ext = if (image.mimeType.contains("png", ignoreCase = true)) "png" else "jpg"
                val name = kotlin.random.Random.nextLong().toString().replace("-", "") + "." + ext
                uploadImageToStorage(
                    "$CLAIM_PROOFS_STORAGE/${user.uid}/$businessId-$name",
                    image.bytes,
                    image.mimeType,
                )
            }
            // The verification-backed claim doc is the source of truth for the admin review queue.
            firestore.collection(CLAIMS).add(
                ClaimCreateDoc(
                    businessId = businessId,
                    businessName = businessName,
                    claimantUid = user.uid,
                    claimantName = claimantName.trim().ifBlank { user.publicName },
                    role = role,
                    contactEmail = contactEmail.trim(),
                    contactPhone = contactPhone.trim(),
                    message = message.trim(),
                    documentRef = documentRef,
                    status = ClaimReviewStatus.Pending,
                    createdAt = Timestamp.ServerTimestamp,
                ),
            )
            // Best-effort: mirror onto the business so the existing "claim requested" UX/gates keep
            // working. Denied (and ignored) when the listing isn't 'unclaimed' — the claim doc still
            // stands, so the admin can act on it regardless.
            runCatching {
                firestore.collection(BUSINESSES).document(businessId).update(
                    "claimStatus" to "claim_requested",
                    "claimRequestedBy" to user.uid,
                )
            }
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    private fun Query.toBusinessesResult(): Flow<DataResult<List<Business>>> =
        snapshots
            .map { snap ->
                val businesses = snap.documents.mapNotNull { doc ->
                    runCatching { doc.decode<BusinessDoc>().toModel(doc.id) }.getOrNull()
                }
                DataResult.Success(businesses) as DataResult<List<Business>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }
}

/**
 * Live [ReviewRepository] over Cloud Firestore (GitLive). Doc id `{authorUid}_{businessId}` is built
 * here (rules enforce it); submitting requires a real (non-anonymous) session — guests get
 * [AppError.Unauthorized] so the UI can route to sign-in. Create writes `ownerReply: null` +
 * `helpfulCount: 0` explicitly (the author-edit rule's `unchanged(...)` checks error on missing keys);
 * edits merge-write only rating/comment/tags.
 */
class FirestoreReviewRepository(
    firebase: FirebaseServices,
    private val auth: AuthService,
) : ReviewRepository {

    private val firestore: FirebaseFirestore = firebase.firestore

    override fun observeVisibleReviews(businessId: String): Flow<DataResult<List<Review>>> =
        firestore.collection(REVIEWS)
            .where { "businessId" equalTo businessId }
            .where { "status" equalTo REVIEW_VISIBLE }
            .orderBy("createdAt", Direction.DESCENDING).limit(100)
            .snapshots
            .map { snap ->
                val reviews = snap.documents
                    .mapNotNull { doc -> runCatching { doc.decode<ReviewReadDoc>().toModel(doc.id) }.getOrNull() }
                    .sortedByDescending { it.createdAtMillis ?: 0L }
                DataResult.Success(reviews) as DataResult<List<Review>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeMyReviews(): Flow<DataResult<List<Review>>> =
        auth.authState.flatMapLatest { user ->
            if (user == null || user.isAnonymous) {
                kotlinx.coroutines.flow.flowOf(DataResult.Success(emptyList()))
            } else {
                firestore.collection(REVIEWS).where { "authorUid" equalTo user.uid }
                    .orderBy("createdAt", Direction.DESCENDING).limit(100)
                    .snapshots
                    .map { snap ->
                        val reviews = snap.documents
                            .mapNotNull { doc -> runCatching { doc.decode<ReviewReadDoc>().toModel(doc.id) }.getOrNull() }
                            .sortedByDescending { it.createdAtMillis ?: 0L }
                        DataResult.Success(reviews) as DataResult<List<Review>>
                    }
                    .catch { emit(DataResult.Failure(it.toAppError())) }
            }
        }

    override suspend fun submitReview(
        businessId: String,
        rating: Int,
        comment: String,
        tags: List<String>,
    ): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        if (rating !in 1..5) return DataResult.Failure(AppError.Unknown())
        return try {
            val doc = firestore.collection(REVIEWS).document("${user.uid}_$businessId")
            // Rules allow get() on the caller's own `{uid}_*` slot even when the doc is missing, but
            // stay resilient against stale rules: a denied exists-check means "treat as new review"
            // (if it actually exists, the create below is rejected by rules instead of us corrupting
            // server-managed fields via a blind overwrite).
            val exists = runCatching { doc.get().exists }.getOrDefault(false)
            withTimeout(WRITE_TIMEOUT_MS) {
                if (exists) {
                    doc.set(ReviewEditDoc(rating = rating, comment = comment, tags = tags), merge = true)
                } else {
                    doc.set(
                        ReviewCreateDoc(
                            businessId = businessId,
                            authorUid = user.uid,
                            authorName = user.publicName,
                            rating = rating,
                            comment = comment,
                            tags = tags,
                            ownerReply = null,
                            helpfulCount = 0,
                            status = REVIEW_VISIBLE,
                            createdAt = Timestamp.ServerTimestamp,
                        ),
                    )
                }
            }
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }
}

/** Live [FollowRepository] over Cloud Firestore: one doc per (user, business) at `{uid}_{businessId}`. */
class FirestoreFollowRepository(
    firebase: FirebaseServices,
    private val auth: AuthService,
) : FollowRepository {

    private val firestore: FirebaseFirestore = firebase.firestore

    // Re-queries on auth changes (flatMapLatest over authState), so following reflects live right
    // after the user signs in — not only when the flow is first created.
    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeFollowing(): Flow<DataResult<Set<String>>> =
        auth.authState.flatMapLatest { user ->
            if (user == null || user.isAnonymous) {
                kotlinx.coroutines.flow.flowOf(DataResult.Success(emptySet()))
            } else {
                firestore.collection(FOLLOWS).where { "userId" equalTo user.uid }.snapshots
                    .map { snap ->
                        val ids = snap.documents
                            .mapNotNull { doc -> runCatching { doc.decode<FollowReadDoc>().businessId }.getOrNull() }
                            .filter { it.isNotBlank() }
                            .toSet()
                        DataResult.Success(ids) as DataResult<Set<String>>
                    }
                    .catch { emit(DataResult.Failure(it.toAppError())) }
            }
        }

    override suspend fun setFollowing(businessId: String, follow: Boolean): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        if (businessId.isBlank()) return DataResult.Failure(AppError.Unknown())
        return try {
            val doc = firestore.collection(FOLLOWS).document("${user.uid}_$businessId")
            if (follow) {
                doc.set(FollowCreateDoc(userId = user.uid, businessId = businessId, createdAt = Timestamp.ServerTimestamp))
            } else {
                doc.delete()
            }
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }
}

/**
 * Live [ListingRequestRepository]. No auth: a public "remove / correct my listing" request must work
 * without an account (privacy law). Writes are shape-locked by rules; admins read/action them.
 */
class FirestoreListingRequestRepository(
    firebase: FirebaseServices,
) : ListingRequestRepository {

    private val firestore: FirebaseFirestore = firebase.firestore

    override suspend fun submit(
        businessId: String,
        businessName: String,
        type: ListingRequestType,
        message: String,
        contactEmail: String?,
    ): DataResult<Unit> {
        if (message.isBlank()) return DataResult.Failure(AppError.Unknown())
        val wireType = when (type) {
            ListingRequestType.Removal -> "removal"
            ListingRequestType.Correction -> "correction"
            ListingRequestType.Privacy -> "privacy"
        }
        return try {
            firestore.collection(LISTING_REQUESTS).add(
                ListingRequestCreateDoc(
                    businessId = businessId.take(200),
                    businessName = businessName.take(200),
                    type = wireType,
                    message = message.trim().take(2000),
                    contactEmail = contactEmail?.trim().orEmpty().take(200),
                    status = "open",
                    createdAt = Timestamp.ServerTimestamp,
                ),
            )
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }
}

/**
 * Live [ListingMetricsRepository]: recording via the recordListingMetric callable (the ONLY writer of
 * `businesses/{id}/metrics/counters`); reads stream the counters doc (owner/admin per rules).
 */
class FirestoreListingMetricsRepository(
    private val firebase: FirebaseServices,
) : ListingMetricsRepository {

    override suspend fun record(businessId: String, metric: ListingMetric) {
        // Best-effort by contract: a lost metric must never surface as a user-facing error.
        runCatching {
            firebase.functions.httpsCallable("recordListingMetric")
                .invoke(mapOf("businessId" to businessId, "metric" to metric.key))
        }
    }

    override fun observeCounters(businessId: String): Flow<DataResult<ListingCounters>> =
        firebase.firestore.collection(BUSINESSES).document(businessId)
            .collection(METRICS).document(METRICS_COUNTERS).snapshots
            .map { snap ->
                val counters = if (snap.exists) snap.decode<ListingCountersDoc>().toModel() else ListingCounters()
                DataResult.Success(counters) as DataResult<ListingCounters>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override fun observeDailyCounters(
        businessId: String,
        sinceDay: String,
    ): Flow<DataResult<List<DailyListingCounters>>> =
        // The `day` filter also excludes the all-time `counters` doc (it has no `day` field).
        firebase.firestore.collection(BUSINESSES).document(businessId)
            .collection(METRICS).where { "day" greaterThanOrEqualTo sinceDay }.snapshots
            .map { snap ->
                val days = snap.documents
                    .mapNotNull { doc -> runCatching { doc.decode<DailyCountersDoc>().toModel() }.getOrNull() }
                    .sortedBy { it.day }
                DataResult.Success(days) as DataResult<List<DailyListingCounters>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }
}

/** Live [CategoryRepository] over Cloud Firestore (GitLive). */
class FirestoreCategoryRepository(
    firebase: FirebaseServices,
) : CategoryRepository {

    private val firestore: FirebaseFirestore = firebase.firestore

    override fun observeCategories(): Flow<DataResult<List<Category>>> =
        firestore.collection(CATEGORIES).snapshots
            .map { snap ->
                val categories = snap.documents
                    .mapNotNull { doc -> runCatching { doc.decode<CategoryDoc>().toModel(doc.id) }.getOrNull() }
                    // Drop malformed docs (no name in any language): CategoryDoc's permissive defaults
                    // otherwise render a stray/partial write as a blank "0 businesses" phantom tile.
                    .filter { it.name.isNotBlank() || it.nameLocalized?.isEmpty == false }
                    .sortedBy { it.order }
                DataResult.Success(categories) as DataResult<List<Category>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }
}
